import { useEffect, useState } from 'react';

/**
 * Holds a loading indicator back until the wait is long enough to be worth
 * acknowledging.
 *
 * A spinner that appears and disappears inside ~200ms reads as a glitch rather
 * than as feedback: the eye catches the change but not the meaning. Warm
 * caches, a 304, or a fast local network all land in that window, so the
 * indicator flashes on exactly the requests that felt instant. Returning false
 * for the first `delayMs` means quick responses render no loading state at all
 * and slow ones still get one promptly.
 *
 * @param {boolean} isLoading whether the work is currently in flight
 * @param {number} delayMs how long to wait before admitting to it
 * @returns {boolean} whether the indicator should be shown
 */
export default function useDeferredLoading(isLoading, delayMs = 250) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setShow(false);
      return undefined;
    }

    const timer = setTimeout(() => setShow(true), delayMs);
    return () => clearTimeout(timer);
  }, [isLoading, delayMs]);

  return show;
}
